import React from 'react';

import type { IntlShape, MessageDescriptor } from 'react-intl';

import type { EditorAnalyticsAPI } from '@atlaskit/editor-common/analytics/api';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics/types/enums';
import commonMessages from '@atlaskit/editor-common/messages';
import {
	toolbarMessages as layoutMessages,
	toolbarMessages,
} from '@atlaskit/editor-common/messages/layout';
import { areToolbarFlagsEnabled } from '@atlaskit/editor-common/toolbar-flag-check';
import type { Command, CommandDispatch } from '@atlaskit/editor-common/types/command';
import type {
	DropdownOptions,
	FloatingToolbarButton,
	FloatingToolbarConfig,
	FloatingToolbarCopyButton,
	FloatingToolbarItem,
	FloatingToolbarSeparator,
	Icon,
} from '@atlaskit/editor-common/types/floating-toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { NodeType, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { findDomRefAtPos } from '@atlaskit/editor-prosemirror/utils';
import { akEditorSelectedNodeClassName } from '@atlaskit/editor-shared-styles/constants';
import CopyIcon from '@atlaskit/icon/core/copy';
import DeleteIcon from '@atlaskit/icon/core/delete';
import LayoutOneColumnIcon from '@atlaskit/icon/core/layout-one-column';
import LayoutThreeColumnsIcon from '@atlaskit/icon/core/layout-three-columns';
import LayoutThreeColumnsSidebarsIcon from '@atlaskit/icon/core/layout-three-columns-sidebars';
import LayoutTwoColumnsIcon from '@atlaskit/icon/core/layout-two-columns';
import LayoutTwoColumnsSidebarLeftIcon from '@atlaskit/icon/core/layout-two-columns-sidebar-left';
import LayoutTwoColumnsSidebarRightIcon from '@atlaskit/icon/core/layout-two-columns-sidebar-right';
import TableColumnsDistributeIcon from '@atlaskit/icon/core/table-columns-distribute';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/editor-experiment';

import type { LayoutPlugin } from '../index';
import {
	deleteActiveLayoutNode,
	distributeLayoutColumns,
	getPresetLayout,
	setPresetLayout,
} from '../pm-plugins/actions';
import { isDistributedUniformly } from '../pm-plugins/utils/layout-column-distribution';
import type { PresetLayout } from '../types';
import {
	EditorLayoutFiveColumnsIcon,
	EditorLayoutFourColumnsIcon,
} from './icons/LayoutColumnsIcon';
import { LayoutThreeWithLeftSidebarsIcon } from './icons/LayoutThreeWithLeftSidebars';
import { LayoutThreeWithRightSidebarsIcon } from './icons/LayoutThreeWithRightSidebars';

type PresetLayoutButtonItem = {
	icon: Icon;
	iconFallback?: Icon;
	id?: string;
	title: MessageDescriptor;
	type: PresetLayout;
};

const LAYOUT_TYPES: PresetLayoutButtonItem[] = [
	{
		id: 'editor.layout.twoEquals',
		type: 'two_equal',
		title: toolbarMessages.twoColumns,
		icon: LayoutTwoColumnsIcon,
	},
	{
		id: 'editor.layout.threeEquals',
		type: 'three_equal',
		title: toolbarMessages.threeColumns,
		icon: LayoutThreeColumnsIcon,
	},
];

const LAYOUT_TYPES_WITH_SINGLE_COL: PresetLayoutButtonItem[] = [
	{
		id: 'editor.layout.singeLayout',
		type: 'single',
		title: toolbarMessages.singleColumn,
		icon: LayoutOneColumnIcon,
	},
	...LAYOUT_TYPES,
];

const SIDEBAR_LAYOUT_TYPES: PresetLayoutButtonItem[] = [
	{
		id: 'editor.layout.twoRightSidebar',
		type: 'two_right_sidebar',
		title: toolbarMessages.rightSidebar,
		icon: LayoutTwoColumnsSidebarRightIcon,
	},
	{
		id: 'editor.layout.twoLeftSidebar',
		type: 'two_left_sidebar',
		title: toolbarMessages.leftSidebar,
		icon: LayoutTwoColumnsSidebarLeftIcon,
	},
	{
		id: 'editor.layout.threeWithSidebars',
		type: 'three_with_sidebars',
		title: toolbarMessages.threeColumnsWithSidebars,
		icon: LayoutThreeColumnsSidebarsIcon,
	},
];

const LAYOUT_WITH_TWO_COL_DISTRIBUTION = [
	{
		id: 'editor.layout.twoRightSidebar',
		type: 'two_right_sidebar',
		title: toolbarMessages.rightSidebar,
		icon: LayoutTwoColumnsSidebarRightIcon,
	},
	{
		id: 'editor.layout.twoLeftSidebar',
		type: 'two_left_sidebar',
		title: toolbarMessages.leftSidebar,
		icon: LayoutTwoColumnsSidebarLeftIcon,
	},
] as const;

const LAYOUT_WITH_THREE_COL_DISTRIBUTION = [
	{
		id: 'editor.layout.threeWithSidebars',
		type: 'three_with_sidebars',
		title: toolbarMessages.threeColumnsWithSidebars,
		icon: LayoutThreeColumnsSidebarsIcon,
	},
	{
		id: 'editor.layout.threeRightSidebars',
		type: 'three_right_sidebars',
		title: toolbarMessages.threeColumnsWithRightSidebars,
		icon: LayoutThreeWithRightSidebarsIcon,
		iconFallback: LayoutThreeWithRightSidebarsIcon,
	},
	{
		id: 'editor.layout.threeLeftSidebars',
		type: 'three_left_sidebars',
		title: toolbarMessages.threeColumnsWithLeftSidebars,
		icon: LayoutThreeWithLeftSidebarsIcon,
		iconFallback: LayoutThreeWithLeftSidebarsIcon,
	},
] as const;

const buildLayoutButton = (
	intl: IntlShape,
	item: PresetLayoutButtonItem,
	currentLayout: string | undefined,
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
): FloatingToolbarItem<Command> => ({
	id: item.id,
	type: 'button',
	icon: item.icon,
	iconFallback: item.iconFallback,
	testId: item.title.id ? `${item.title.id}` : undefined,
	title: intl.formatMessage(item.title),
	onClick: setPresetLayout(editorAnalyticsAPI)(item.type),
	selected: !!currentLayout && currentLayout === item.type,
	tabIndex: null,
});

export const layoutToolbarTitle = 'Layout floating controls';

const iconPlaceholder = <LayoutTwoColumnsIcon label="" />; // TODO: ED-25466 - Replace with proper icon

const getLayoutColumnsIcons = (colCount: number) => {
	if (
		!editorExperiment('single_column_layouts', true) &&
		!editorExperiment('platform_editor_controls', 'variant1')
	) {
		return undefined;
	}

	switch (colCount) {
		case 1:
			return <LayoutOneColumnIcon label="" />;
		case 2:
			return <LayoutTwoColumnsIcon label="" />;
		case 3:
			return <LayoutThreeColumnsIcon label="" />;
		case 4:
			return <EditorLayoutFourColumnsIcon />;
		case 5:
			return <EditorLayoutFiveColumnsIcon />;
		default:
			return undefined;
	}
};

const getLayoutColumnWidths = (node: PMNode): number[] => {
	return node.children.map((child) => child.attrs.width);
};

const getAdvancedLayoutItems = ({
	addSidebarLayouts,
	intl,
	editorAnalyticsAPI,
	node,
	nodeType,
	separator,
	deleteButton,
	currentLayout,
	allowAdvancedSingleColumnLayout,
}: {
	addSidebarLayouts: boolean;
	allowAdvancedSingleColumnLayout: boolean;
	currentLayout: string | undefined;
	deleteButton: FloatingToolbarButton<Command>;
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined;
	intl: IntlShape;
	node: PMNode;
	nodeType: NodeType;
	separator: FloatingToolbarSeparator;
}) => {
	const numberOfColumns = node.content.childCount || 2;
	const distributionOptions =
		numberOfColumns === 2
			? LAYOUT_WITH_TWO_COL_DISTRIBUTION
			: numberOfColumns === 3
				? LAYOUT_WITH_THREE_COL_DISTRIBUTION
				: [];

	const columnOptions: DropdownOptions<Command> = [
		{
			title: intl.formatMessage(layoutMessages.columnOption, { count: 2 }), //'2-columns',
			icon: getLayoutColumnsIcons(2) || iconPlaceholder,
			onClick: setPresetLayout(editorAnalyticsAPI)('two_equal'),
			selected: numberOfColumns === 2,
		},
		{
			title: intl.formatMessage(layoutMessages.columnOption, { count: 3 }), //'3-columns'
			icon: getLayoutColumnsIcons(3) || iconPlaceholder,
			onClick: setPresetLayout(editorAnalyticsAPI)('three_equal'),
			selected: numberOfColumns === 3,
		},
		{
			title: intl.formatMessage(layoutMessages.columnOption, { count: 4 }), //'4-columns'
			icon: getLayoutColumnsIcons(4) || iconPlaceholder,
			onClick: setPresetLayout(editorAnalyticsAPI)('four_equal'),
			selected: numberOfColumns === 4,
		},
		{
			title: intl.formatMessage(layoutMessages.columnOption, { count: 5 }), //'5-columns'
			icon: getLayoutColumnsIcons(5) || iconPlaceholder,
			onClick: setPresetLayout(editorAnalyticsAPI)('five_equal'),
			selected: numberOfColumns === 5,
		},
	];

	const dropdownOptions: DropdownOptions<Command> = [
		...(allowAdvancedSingleColumnLayout
			? [
					{
						title: intl.formatMessage(layoutMessages.columnOption, { count: 1 }), //'1-columns',
						icon: getLayoutColumnsIcons(1) || iconPlaceholder,
						onClick: setPresetLayout(editorAnalyticsAPI)('single'),
						selected: numberOfColumns === 1,
					},
				]
			: []),
		...columnOptions,
	];

	const distributeColumnsButton: FloatingToolbarButton<Command> | undefined =
		numberOfColumns > 1
			? {
					disabled: isDistributedUniformly(getLayoutColumnWidths(node)),
					icon: TableColumnsDistributeIcon,
					onClick: (editorState: EditorState, dispatch: CommandDispatch | undefined) => {
						const tr = distributeLayoutColumns(editorAnalyticsAPI)({
							inputMethod: INPUT_METHOD.FLOATING_TB,
							target: 'allColumns',
						})({ tr: editorState.tr });

						if (!tr) {
							return false;
						}

						dispatch?.(tr);

						return true;
					},
					testId: 'layout-distribute-columns',
					title: intl.formatMessage(layoutMessages.distributeColumns),
					type: 'button',
				}
			: undefined;

	return [
		{
			type: 'dropdown',
			title: intl.formatMessage(layoutMessages.columnOption, { count: numberOfColumns }), //`${numberOfColumns}-columns`,
			options: dropdownOptions,
			showSelected: true,
			testId: 'column-options-button',
		},
		...(distributionOptions.length > 0 || distributeColumnsButton ? [separator] : []),
		...(addSidebarLayouts
			? distributionOptions.map((i) =>
					buildLayoutButton(intl, i, currentLayout, editorAnalyticsAPI),
				)
			: []),
		...(distributeColumnsButton ? [distributeColumnsButton] : []),
	] as FloatingToolbarItem<Command>[];
};

const fullHeightSeparator: FloatingToolbarSeparator = {
	type: 'separator',
	fullHeight: true,
};

export const buildToolbar = (
	state: EditorState,
	intl: IntlShape,
	pos: number,
	_allowBreakout: boolean,
	addSidebarLayouts: boolean,
	// This is the old allowSingleColumnLayout param which will be removed in the future
	// It is kept for backwards compatibility and avoid unepxected behavior
	allowSingleColumnLayout: boolean,
	allowAdvancedSingleColumnLayout: boolean,
	api: ExtractInjectionAPI<LayoutPlugin> | undefined,
): FloatingToolbarConfig | undefined => {
	const { hoverDecoration } = api?.decorations?.actions ?? {};
	const editorAnalyticsAPI = api?.analytics?.actions;
	const node = state.doc.nodeAt(pos);
	const toolbarFlagsEnabled = areToolbarFlagsEnabled(Boolean(api?.toolbar));

	if (node) {
		const currentLayout = getPresetLayout(node);

		const separator: FloatingToolbarSeparator = {
			type: 'separator',
		};

		const nodeType = state.schema.nodes.layoutSection;

		const deleteButton: FloatingToolbarButton<Command> = {
			id: 'editor.layout.delete',
			type: 'button',
			appearance: 'danger',
			focusEditoronEnter: true,
			icon: DeleteIcon,
			testId: commonMessages.remove.id,
			title: intl.formatMessage(commonMessages.remove),
			onClick: deleteActiveLayoutNode(editorAnalyticsAPI, INPUT_METHOD.FLOATING_TB),
			onMouseEnter: hoverDecoration?.(nodeType, true),
			onMouseLeave: hoverDecoration?.(nodeType, false),
			onFocus: hoverDecoration?.(nodeType, true),
			onBlur: hoverDecoration?.(nodeType, false),
			tabIndex: null,
		};

		const copyButton: FloatingToolbarCopyButton = {
			type: 'copy-button',
			items: [
				{
					state,
					formatMessage: intl.formatMessage,
					nodeType,
				},
			],
		};

		const layoutTypes = allowSingleColumnLayout ? LAYOUT_TYPES_WITH_SINGLE_COL : LAYOUT_TYPES;
		const hoverDecorationProps = (nodeType: NodeType | NodeType[], className?: string) => ({
			onMouseEnter: hoverDecoration?.(nodeType, true, className),
			onMouseLeave: hoverDecoration?.(nodeType, false, className),
			onFocus: hoverDecoration?.(nodeType, true, className),
			onBlur: hoverDecoration?.(nodeType, false, className),
		});
		// testId is required to show focus on trigger button on ESC key press
		// see hideOnEsc in platform/packages/editor/editor-plugin-floating-toolbar/src/ui/Dropdown.tsx
		const testId = 'layout-overflow-dropdown-trigger';

		const overflowMenu: FloatingToolbarItem<Command> = {
			type: 'overflow-dropdown',
			testId,
			options: [
				{
					title: intl.formatMessage(commonMessages.copyToClipboard),
					onClick: () => {
						api?.core?.actions.execute(
							// @ts-ignore
							api?.floatingToolbar?.commands.copyNode(nodeType, INPUT_METHOD.FLOATING_TB),
						);
						return true;
					},
					icon: <CopyIcon label="" />,
					...hoverDecorationProps(nodeType, akEditorSelectedNodeClassName),
				},
				{
					title: intl.formatMessage(commonMessages.delete),
					onClick: deleteActiveLayoutNode(editorAnalyticsAPI, INPUT_METHOD.FLOATING_TB),
					icon: <DeleteIcon label="" />,
					...hoverDecorationProps(nodeType),
				},
			],
		};
		return {
			title: layoutToolbarTitle,
			// Ignored via go/ees005
			// eslint-disable-next-line @atlaskit/editor/no-as-casting
			getDomRef: (view) => findDomRefAtPos(pos, view.domAtPos.bind(view)) as HTMLElement,
			nodeType,
			groupLabel: intl.formatMessage(toolbarMessages.floatingToolbarRadioGroupAriaLabel),
			items: [
				...(editorExperiment('advanced_layouts', true)
					? getAdvancedLayoutItems({
							addSidebarLayouts,
							intl,
							editorAnalyticsAPI,
							nodeType,
							node,
							separator,
							deleteButton,
							currentLayout,
							allowAdvancedSingleColumnLayout,
						})
					: [
							...layoutTypes.map((i) =>
								buildLayoutButton(intl, i, currentLayout, editorAnalyticsAPI),
							),
							...(addSidebarLayouts
								? SIDEBAR_LAYOUT_TYPES.map((i) =>
										buildLayoutButton(intl, i, currentLayout, editorAnalyticsAPI),
									)
								: []),
						]),

				...(toolbarFlagsEnabled
					? [fullHeightSeparator, overflowMenu]
					: [separator, copyButton, separator, deleteButton]),
			],
			scrollable: true,
		};
	}
	return;
};
